import { Overlay } from '@angular/cdk/overlay';
import {
  AfterViewChecked,
  ApplicationRef,
  Component,
  DestroyRef,
  NgZone,
  OnDestroy,
  OnInit,
  Renderer2,
  RendererStyleFlags2,
  ViewChild,
  ViewContainerRef,
} from '@angular/core';
import { NavigationEnd, NavigationStart, Router } from '@angular/router';
import { SwUpdate, VersionReadyEvent } from '@angular/service-worker';
import { JwtHelperService } from '@auth0/angular-jwt';
import {
  SignInWithApple,
  SignInWithAppleOptions,
  SignInWithAppleResponse,
} from '@capacitor-community/apple-sign-in';
import {
  App,
  BackButtonListenerEvent,
  URLOpenListenerEvent,
} from '@capacitor/app';
import { MatDialog } from '@angular/material/dialog';
import {
  Capacitor,
  PluginListenerHandle,
  SystemBars,
  SystemBarsStyle,
  SystemBarType,
} from '@capacitor/core';
import { Device, DeviceInfo } from '@capacitor/device';
import { Keyboard } from '@capacitor/keyboard';
import {
  LocalNotifications,
  LocalNotificationSchema,
} from '@capacitor/local-notifications';
import { SplashScreen } from '@capacitor/splash-screen';
import { EdgeToEdge } from '@capawesome/capacitor-android-edge-to-edge-support';
import {
  GoogleLoginResponseOnline,
  SocialLogin,
} from '@capgo/capacitor-social-login';
import { Platform } from '@ionic/angular';
import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { SafeArea } from 'capacitor-plugin-safe-area';
import { ToastrService } from 'ngx-toastr';
import { combineLatest, fromEvent, interval, Observable, of } from 'rxjs';
import {
  catchError,
  distinctUntilChanged,
  filter,
  map,
  take,
} from 'rxjs/operators';
import { APPMODE } from 'src/environments/appmode.enum';
import { environment } from '../environments/environment';
import { defaultPaging } from './api/types/Pageable';
import { NewServiceWorkerVersion } from './application-state/+state/application-state.actions';
import {
  AutoprintAppStateChanged,
  OrderDirectPrint,
} from './printers/+state/printers.actions';
import { HelperService } from './public/helper.service';
import { StoreService } from './public/store/store.service';
import { ClearIntegrationPayment } from './public/payments/+state/payment.actions';
import {
  SaveStoreByAlias,
  SaveStoreByAliasFailed,
  SaveStoreByAliasSuccess,
  SetMobileStore,
  StoreActionType,
} from './public/store/+state/stores.actions';
import { LogService } from './shared/logging/LogService';
import { MessageService } from './shared/message.service';
import { MobileStorageService } from './stores/mobile-storage.service';
import { MobilePushNotificationService } from './shared/mobile-push-notification.service';
import { LocationService } from './public/location.service';
import { TrackingService } from './public/tracking.service';
import { AiWidgetBridgeService } from './shared/ai-widget-bridge.service';
import { ViewportService } from './shared/viewport.service';
import { WhitelabelService } from './shared/whitelabel.service';
import { AdminBarcodeScannerService } from './stores/store-events-bookings/stores-events-barcode-scanning-modal/admin-barcode-scanner.service';
import {
  ClientStore,
  Order,
  StoreSaveMode,
  StoreViewState,
} from './stores/stores';
import { StoresService } from './stores/stores.service';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  getCurrentCartState,
  getCurrentStoreViewStatus,
  getSelectedStore,
} from './public/store/+state/stores.selectors';
import { PaymentRedirectService } from './public/payments/payment-redirect.service';
import { PromotionData } from './public/store/types/PromotionData';
import { InitialViewOrchestratorService } from './public/store/services/initial-view-orchestrator.service';
import { startPageRoute } from './public/store/utils/WidgetPageUtils';
import { AdminVoucherScannerBackButtonService } from './public/store/store-checkout/store-checkout-admin-voucher/admin-voucher-scanner-back-button.service';

export let browserRefresh = false;

/**
 * What `Color.parseColor` will accept on Android, restricted to the hex forms.
 *
 * The tint has to be validated before it is handed to the plugin, because an
 * unparseable colour does not degrade to an untinted banner — it aborts the
 * whole notification. See `orderNotificationIconColour`.
 */
const ANDROID_HEX_COLOUR = /^#(?:[0-9a-fA-F]{6}|[0-9a-fA-F]{8})$/;

/**
 * Capacitor Preferences keys shared with the Android OrderPushMessagingService
 * that capacitor-build-utils.js writes into the admin app (card #4514). Keep in
 * step with BRANDING_KEY and NOTIFIED_KEY there.
 */
const ORDER_NOTIFICATION_BRANDING_KEY = 'orderNotificationBranding';
const ORDER_NOTIFIED_NATIVELY_KEY = 'orderNotifiedNatively';
/** How long the native service's note of a shown order stays good. */
const ORDER_NOTIFIED_NATIVELY_TTL_MS = 15 * 60 * 1000;

/**
 * What aapt2 accepts as a resource entry name, which is what the notification
 * plugin looks the icon up by. See `notificationBrandIcon`.
 */
const ANDROID_RESOURCE_NAME = /^[a-z][a-z0-9_]*$/;

/**
 * Views where the customer is placing an order. A promotion tap for another
 * store does not move the customer away from them (#11620, brand apps).
 */
const ORDER_IN_PROGRESS_VIEWS: ReadonlyArray<StoreViewState['state']> = [
  'PRECHECKOUT',
  'CHECKOUT',
  'CHECKOUT_RECOMMENDATIONS',
  'PAYMENTPAY',
  'PAYMENTPROGRESS',
  'PAYMENTPROGRESSERROR',
  'QRCODEPAY',
  'STANDALONEPAYMENT',
  'CONFIRMATIONFAILEDPAGE',
];

interface QueryParams {
  [key: string]: string | number | boolean;
}

interface DeeplinkRoute {
  appEnvironment: string;
  fragment: string;
  path: string;
  queryParams: QueryParams;
  storeAlias: string;
  url: string;
}
interface SafeAreaInsets {
  top: number;
  bottom: number;
  left: number;
  right: number;
}

/**
 * The bottom-anchored chrome the AI Ordering Assistant bubble has to sit clear
 * of. Card #11285.
 *
 * All three are `position: fixed`, all three are bottom-anchored, and all three
 * carry a lower `z-index` than the widget's 9999 — so wherever the widget's box
 * reaches one of them it swallows its taps:
 *
 *   `#customer-footer-wrapper`  GoApp customer navigation (Home / store / Recents),
 *                               z-index 1000  — public-layout.component.scss
 *   `nav.bottom-bar`            template-app tab bar (story #10267),
 *                               z-index 999   — bottom-bar.component.scss
 *   `#button-wrapper`           View Order / Pay Online, appears with the first
 *                               cart item, z-index 999
 *                                             — view-order-button.component.scss
 *
 * Measured from the rendered boxes rather than recomputed from those components'
 * own offsets, because there is no single formula left to reuse: the customer
 * footer anchors on `max(env(safe-area-inset-bottom), var(--safe-area-inset-bottom))`,
 * the View Order button on the raw `--safe-area-inset-bottom` only — so the two
 * already disagree by the env() inset on any web build, where the Capacitor
 * plugin never writes the variable — while the tab bar reserves
 * `--app-safe-area-inset-bottom` instead. On top of that the footer's height is
 * carried in NgRx rather than in CSS, it grows when a long store name wraps, and
 * `StorePageComponent` overwrites the View Order button's `bottom` outright with
 * a bare pixel value carrying neither an inset nor the cookie-bar term. A fourth
 * copy of the arithmetic here would inherit every one of those disagreements.
 * The boxes on screen are the one thing all of them already agree on.
 *
 * The cookie bars are deliberately absent. `#sticky-cookie` is not a consent
 * notice: it is shown whenever the cart is empty and is dismissed by a tap
 * anywhere on the catalog, so tracking it would lift the bubble on the ordinary
 * web storefront and then drop it back out from under the customer's finger on
 * their first tap. `#cookie-bar` (Accept / Reject) genuinely is clipped by the
 * bubble, but it is a separate defect to raise, not one to fold in here — and
 * whenever the View Order button is on screen it already sits above both bars,
 * so clearing it clears them too.
 *
 * Matched with `querySelectorAll`, not `getElementById`: the LIST and TILES
 * catalog layouts each render their own `app-view-order-button`, so
 * `#button-wrapper` is not unique in the document and the visible instance is
 * not reliably the first one.
 */
const BOTTOM_CHROME_SELECTORS = [
  '#customer-footer-wrapper',
  'nav.bottom-bar',
  '#button-wrapper',
].join(', ');

/** The widget iframe itself, whose column decides what can actually block it. */
const AI_WIDGET_SELECTOR = '.ai-widget-frame';

/** Published on the document root for `.ai-widget-frame` (app.component.scss). */
const BOTTOM_CHROME_HEIGHT_VARIABLE = '--app-bottom-chrome-height';

/**
 * Floor on the viewport left above the widget, so a pathological stack of
 * chrome can never push the bubble off the top of the screen. 160px covers the
 * 88px box, its 16px offset and enough margin that it cannot end up under a
 * status bar on an edge-to-edge device. Sized so it cannot engage on any real
 * stack — the shortest viewport in play, a landscape phone, still clears an
 * inset plus a nav bar plus the View Order button — so if it ever does engage
 * the chrome is pathological, and staying reachable then beats clearing it.
 */
const MIN_AI_WIDGET_HEADROOM = 160;

@Component({
  // tslint:disable-next-line
  // eslint-disable-next-line @angular-eslint/component-selector
  selector: 'body',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
})
export class AppComponent implements OnInit, OnDestroy, AfterViewChecked {
  jwtHelper = new JwtHelperService();
  title = 'GonnaOrder';
  isIOSApp: boolean;
  isGoAdminApp: boolean;
  loader: boolean = false;
  PushNotifications: any;
  private keyboardShowListener: Promise<PluginListenerHandle>;
  iosSafeAreaOverflowHeight: number = 0;
  iosSafeAreaBottomHeight: number = 0;
  isKeyboardOpen: boolean = false;
  storesAiSafeUrl: SafeResourceUrl = null;
  isOrdersAiEnabled: boolean = false;
  // True while the AI Ordering Assistant chat panel is open. Drives the
  // `.expanded` modifier on the `.ai-widget-frame` iframe (see
  // app.component.scss) so the iframe element wraps only the visible
  // chat-bubble icon while collapsed and grows to the full panel size
  // only while expanded. State is driven by AI_ORDER_LAUNCHER_STATE
  // postMessage envelopes emitted by the iframe content (see
  // ngOnInit handler and the matching contract in
  // stores-ai-ordering-frontend/src/bridge/postToHost.ts). Card #9733.
  aiWidgetLauncherState: 'collapsed' | 'launcher' | 'chat' = 'collapsed';
  currentOrderUuid: string = '';
  storeViewState: StoreViewState;

  /**
   * Where ngx-color-picker renders a picker opened with
   * `cpUseRootViewContainer`: it looks the root component up and uses its
   * `vcRef`. The anchor sits at the end of this template rather than on the
   * host, because the host is `<body>` and a container on it would insert
   * the picker after `<body>`, under `<html>`.
   */
  @ViewChild('rootViewContainer', { read: ViewContainerRef, static: true })
  vcRef: ViewContainerRef;

  /**
   * The store alias this template app was built for, read once at start.
   * `environment.templateStoreAlias` itself is overwritten when the customer
   * switches to a child store (store-child-list, store-sibling-selector,
   * multi-store-checkout), so it cannot tell the app's own store apart later.
   */
  private readonly builtForStoreAlias = environment.templateStoreAlias;

  private keyboardHideListener: Promise<PluginListenerHandle>;
  private androidKeyboardListeners: Promise<PluginListenerHandle>[] = [];
  private resizeListener: () => void;
  private scrollTimeout: any;
  private keyboardShowTimeout: any;
  private keyboardHideTimeout: any;
  private safeAreaListener: Promise<PluginListenerHandle>;
  private appStateChangeListener: Promise<PluginListenerHandle>;
  private safeAreaInsets: SafeAreaInsets = {
    top: 0,
    bottom: 0,
    left: 0,
    right: 0,
  };

  constructor(
    private router: Router,
    private swUpdate: SwUpdate,
    private appRef: ApplicationRef,
    private store: Store<any>,
    private viewportService: ViewportService,
    private actions$: Actions,
    private ngZone: NgZone,
    private toastr: ToastrService,
    private translateSer: TranslateService,
    private platform: Platform,
    private helperService: HelperService,
    private trackingService: TrackingService,
    private whitelabelService: WhitelabelService,
    private logger: LogService,
    private storesService: StoresService,
    private renderer: Renderer2,
    private messageService: MessageService,
    private mobileNotificationService: MobilePushNotificationService,
    private adminBarcodeScannerService: AdminBarcodeScannerService,
    private overlay: Overlay,
    private sanitizer: DomSanitizer,
    private destroyRef: DestroyRef,
    private paymentRedirectService: PaymentRedirectService,
    private locationService: LocationService,
    private storeService: StoreService,
    private mobileStorage: MobileStorageService,
    private initialViewOrchestrator: InitialViewOrchestratorService,
    private voucherScannerBackButton: AdminVoucherScannerBackButtonService,
    private dialog: MatDialog,
    // Injected to eagerly construct the bridge so its store subscription
    // starts running at app startup. The service is a per-store-gated no-op
    // for tenants/stores that have not enabled the AI Ordering Assistant.
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    private aiWidgetBridgeService: AiWidgetBridgeService,
  ) {
    this.platform.ready().then(() => {
      this.initializeApp();
    });
    this.isIOSApp = Capacitor.getPlatform() === 'ios';
    this.isGoAdminApp = helperService.isGoAdminApp();

    if (environment.mode !== APPMODE.Web) {
      this.platform.ready().then(async () => {
        // First, and outside the push-notification branch below. Recovery exists
        // precisely for the case where the push path misbehaved, so it must not
        // depend on the push listeners being reached — nor on the splash-screen
        // and system-bar awaits that follow, any one of which would skip the
        // rest of this callback if it rejected. Card #4514.
        this.registerAutoprintReconciliation();

        // Story #11856: an app with a start page listens for links before the
        // Android splash, so a link that opens a page of its own is known
        // before the store loads, whether it launched the app or reached an
        // activity recreated after the process was killed. The link's own
        // navigation still waits for the boot navigation (see
        // initializeDeepLinks). The other apps keep their order.
        const linksBeforeSplash = this.listensForLinksBeforeSplash();
        if (linksBeforeSplash) {
          this.initializeDeepLinks();
        }

        if (Capacitor.getPlatform() === 'android') {
          await SplashScreen.show({
            showDuration: 3000,
            autoHide: true,
          });

          // Android 16 enforces edge-to-edge. The capawesome plugin applies the
          // system-bar insets to the WebView natively, keeping content boxed
          // between the bars (no clipping). Bar colors come from capacitor.config
          // (EdgeToEdge.statusBarColor / navigationBarColor).
          //
          // This call MUST stay gated by SDK level. It applies the system-bar
          // insets as MARGINS on the WebView, computed from the UNCONSUMED root
          // window insets, so it cannot tell whether anything upstream already
          // inset the content:
          //   API >= 35 — the platform forces `decorFitsSystemWindows = false`,
          //     nothing pads or consumes, insets propagate to the WebView and
          //     this call is what boxes the content. Needed. (Measured on
          //     Android 16: viewport inset stays 99px across the call.)
          //   API <= 34 — `decorFitsSystemWindows` stays true, so the decor's
          //     LinearLayout (screen_simple.xml, fitsSystemWindows="true") has
          //     ALREADY padded the content and consumed the insets. This call
          //     then adds the same insets a SECOND time. (Measured on Android 14:
          //     viewport inset jumps 82px -> 163px the instant it lands — the
          //     double safe area.) The plugin's own top guard cannot catch it:
          //     it probes android.R.id.content, which is not the view that got
          //     padded, and there is no bottom guard at all.
          // On API <= 34 the bars are real system bars and take their colour
          // from AppTheme.NoActionBar (statusBarColor / navigationBarColor,
          // injected by config-<app>-app.yaml), so skipping this loses nothing.
          const { androidSDKVersion } = await Device.getInfo();
          if ((androidSDKVersion ?? 0) >= 35) {
            await EdgeToEdge.enable();
            this.listenToAndroidKeyboardInset();
          }
          // The plugin sets bar COLORS but not icon contrast — set it per bar:
          // light icons on the black status bar, dark icons on the white nav bar.
          await SystemBars.setStyle({
            style: SystemBarsStyle.Dark,
            bar: SystemBarType.StatusBar,
          });
          await SystemBars.setStyle({
            style: SystemBarsStyle.Light,
            bar: SystemBarType.NavigationBar,
          });
        }
        if (!linksBeforeSplash) {
          this.initializeDeepLinks();
        }
        if (this.handlesBackForStartPage()) {
          this.listenToBackButtonInStartPageApp();
        }

        if (helperService.isGoAdminApp()) {
          this.PushNotifications =
            this.mobileNotificationService.PushNotification;

          this.PushNotifications.addListener(
            'pushNotificationReceived',
            (notification) => this.pushNotificationReceived(notification),
          );
          this.PushNotifications.addListener(
            'pushNotificationActionPerformed',
            (action) => this.pushNotificationActionPerformed(action),
          );

          // Android draws its own banner now that the FCM payload is data-only,
          // so taps arrive here rather than on pushNotificationActionPerformed.
          LocalNotifications.addListener(
            'localNotificationActionPerformed',
            (action) => this.localNotificationActionPerformed(action),
          );

          if (helperService.isAndroid()) {
            void this.rememberOrderNotificationBranding();
          }
        }

        // Promotion push notifications in a template app (#11620). Its own
        // branch, deliberately not the admin one above: the admin handler
        // navigates to orders and prints, neither of which a customer app has.
        if (helperService.isGoTemplateApp()) {
          this.PushNotifications =
            this.mobileNotificationService.PushNotification;

          this.PushNotifications.addListener(
            'pushNotificationActionPerformed',
            (action) => this.promotionNotificationActionPerformed(action),
          );

          // No pushNotificationReceived listener: the story wants no in-app
          // banner. Template builds set presentationOptions ['sound','alert']
          // (Android) and ['badge','sound','alert'] (iOS) in
          // capacitor-build-utils.js, so the phone shows the notification even
          // with the app open, and tapping it fires the listener above.

          void this.enablePromotionNotifications();
        }
      });
    }

    router.events.subscribe((event) => {
      if (event instanceof NavigationStart) {
        browserRefresh = !router.navigated;
      }
    });
  }

  private async initializeApp() {
    const deviceInfo = await Device.getInfo();
    if ((deviceInfo as unknown as DeviceInfo).platform !== 'web') {
      try {
        await SocialLogin.initialize({
          google: {
            webClientId: environment.googleClientId,
            iOSClientId: environment.googleClientId,
            mode: 'online',
          },
          facebook: {
            appId: environment.facebookClientId,
            clientToken: environment.facebookClientToken,
          },
        });
      } catch (error) {
        console.error('Error initializing Google Auth:', error);
      }
    } else {
      console.warn('Google Auth not initialized: Unsupported platform');
    }
  }
  private updateViewportHeight(): void {
    const vh = window.innerHeight * 0.01;
    document.documentElement.style.setProperty('--vh', `${vh}px`);
  }

  private scrollSpecialNoteWithinItemDetailsContainer(
    alignToTop: boolean = false,
  ): boolean {
    const specialNoteElement = document.getElementById('specialNote');
    if (!(specialNoteElement instanceof HTMLTextAreaElement)) {
      return false;
    }

    const scrollableContainer = specialNoteElement.closest('.overflow-auto');
    return this.helperService.scrollElementIntoContainer({
      container:
        scrollableContainer instanceof HTMLElement ? scrollableContainer : null,
      element: specialNoteElement,
      alignToTop,
      behavior: 'auto',
    });
  }
  async ngOnInit() {
    this.resizeListener = () => this.updateViewportHeight();
    window.addEventListener('resize', this.resizeListener);

    this.addScrollListener();

    if (
      this.helperService.isMobileApp() &&
      this.whitelabelService.getEventManagementEnabled()
    ) {
      await this.adminBarcodeScannerService.requestPermissions();
    }

    if (this.isIOSApp) {
      this.keyboardShowListener = Keyboard.addListener(
        'keyboardDidShow',
        () => {
          this.ngZone.run(() => {
            this.isKeyboardOpen = true;
            this.applySafeAreaInsets();
          });
          // Clear any existing timeout before setting new one
          if (this.keyboardShowTimeout) {
            cancelAnimationFrame(this.keyboardShowTimeout);
          }
          this.keyboardShowTimeout = window.requestAnimationFrame(() => {
            const activeElement = document.activeElement;
            if (activeElement instanceof HTMLElement) {
              if (
                activeElement.tagName === 'TEXTAREA' &&
                activeElement.id === 'specialNote'
              ) {
                const wasContainerScrollHandled =
                  this.scrollSpecialNoteWithinItemDetailsContainer(true);
                if (!wasContainerScrollHandled) {
                  activeElement.scrollIntoView({
                    behavior: 'smooth',
                    block: 'center',
                    inline: 'nearest',
                  });
                } else {
                  window.requestAnimationFrame(() => {
                    this.scrollSpecialNoteWithinItemDetailsContainer(true);
                  });
                  if (window.visualViewport) {
                    const syncWithViewport = () => {
                      this.scrollSpecialNoteWithinItemDetailsContainer(true);
                    };
                    window.visualViewport.addEventListener(
                      'resize',
                      syncWithViewport,
                      { once: true },
                    );
                    window.visualViewport.addEventListener(
                      'scroll',
                      syncWithViewport,
                      { once: true },
                    );
                  }
                }
              } else {
                activeElement.scrollIntoView({
                  behavior: 'auto',
                  block: 'nearest',
                  inline: 'nearest',
                });
              }
            }
            this.keyboardShowTimeout = null;
          });
        },
      );

      this.keyboardHideListener = Keyboard.addListener(
        'keyboardDidHide',
        () => {
          this.ngZone.run(() => {
            this.isKeyboardOpen = false;
            this.applySafeAreaInsets();
          });
          // Clear any existing timeout before setting new one
          if (this.keyboardHideTimeout) {
            cancelAnimationFrame(this.keyboardHideTimeout);
          }
          this.keyboardHideTimeout = window.requestAnimationFrame(() => {
            this.updateViewportHeight();
            this.keyboardHideTimeout = null;
          });
        },
      );
    }

    //Message Service For Sign In
    this.messageService.receiveMessage().subscribe(async (message) => {
      if (message === 'appleSignIn') {
        this.appleSignIn();
      } else if (message === 'googleSignIn') {
        this.signInWithGoogle();
      } else if (message === 'facebookLogin') {
        this.facebookSignIn();
      }
    });
    console.log('checking for app version');
    if (this.swUpdate.isEnabled) {
      console.log('service worker enabled');

      this.ngZone.runOutsideAngular(() => {
        interval(30 * 1000).subscribe(() => {
          this.ngZone.run(() => {
            console.log('checking for updates');
            this.swUpdate.checkForUpdate();
          });
        });

        this.swUpdate.versionUpdates
          .pipe(
            filter(
              (evt): evt is VersionReadyEvent => evt.type === 'VERSION_READY',
            ),
          )
          .subscribe((evt) => {
            this.ngZone.run(() => {
              console.log('New version detected');
              console.log('Current:', evt.currentVersion.hash);
              console.log('Latest:', evt.latestVersion.hash);

              this.store.dispatch(
                new NewServiceWorkerVersion(
                  evt.currentVersion.hash,
                  evt.latestVersion.hash,
                ),
              );

              this.swUpdate.activateUpdate().then(() => {
                console.log('Update activated');
              });
            });
          });
      });
    }

    this.router.events.subscribe((evt) => {
      if (!(evt instanceof NavigationEnd)) {
        return;
      }
      this.trackingService.trackMetaPageViewIfUrlChanged();
      window.scrollTo(0, 0);
    });

    // disable zoom in for ios
    this.viewportService.applyCustomViewportForIOS();
    this.viewportService.applyResizesContentViewportForAndroidTemplateApp();

    if (
      environment.mode == APPMODE.Web ||
      environment.mode == APPMODE.Admin ||
      environment.mode == APPMODE.Template ||
      environment.mode == APPMODE.Customer ||
      environment.mode == APPMODE.Kiosk
    ) {
      this.title = this.whitelabelService.getWhiteLabelName();
      if (document.readyState !== 'loading') {
        this.setBrandColors();
      } else {
        document.addEventListener('DOMContentLoaded', () =>
          this.setBrandColors(),
        );
      }
    }

    if (this.isIOSApp) {
      SafeArea.getSafeAreaInsets().then(
        ({ insets }: { insets: SafeAreaInsets }) => {
          this.setSafeAreaInsets(insets);
        },
      );

      // Track SafeArea listener for cleanup
      this.safeAreaListener = SafeArea.addListener(
        'safeAreaChanged',
        (data: { insets: SafeAreaInsets }) => {
          const { insets } = data;
          this.setSafeAreaInsets(insets);
        },
      );
    }

    this.isOrdersAiEnabled = this.whitelabelService.isTenantOrdersAiEnabled();

    combineLatest([
      this.store.select(getSelectedStore),
      this.store.select(getCurrentCartState),
    ])
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: ([selectedStore, cartState]) => {
          const shouldShow = this.shouldShowOrdersAi(selectedStore);

          if (shouldShow && this.currentOrderUuid != cartState.uuid) {
            this.setupStoresAi(selectedStore, cartState);
            return;
          }

          if (!shouldShow && this.storesAiSafeUrl) {
            // Settings flipped to "hide" while the widget was on screen
            // (e.g. admin disabled Ordering or Shopping Basket
            // mid-session). Tear down the iframe and reset the uuid
            // guard so the widget rebuilds cleanly if the toggles
            // flip back on. See Trello #9734.
            this.storesAiSafeUrl = null;
            this.currentOrderUuid = '';
          }
        },
      });

    this.store
      .select(getCurrentStoreViewStatus)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        distinctUntilChanged((prev, curr) => {
          return prev?.state == curr?.state;
        }),
      )
      .subscribe((storeViewState) => (this.storeViewState = storeViewState));

    // Listen for launcher state messages from the AI Ordering Assistant
    // iframe so we can grow / shrink the iframe element's bounding box.
    // The iframe content (stores-ai-ordering-frontend) emits an
    // AI_ORDER_LAUNCHER_STATE envelope whenever the chat opens or closes,
    // plus once at mount with the initial state. Card #9733.
    //
    // Origin validation: the iframe is served from
    // `order-ai.<whitelabel-domain>` (see setupStoresAi below). We reject
    // any postMessage whose origin doesn't match, which closes the door
    // on a malicious same-window page trying to flip our class. The
    // listener is registered unconditionally — the typed discriminator
    // is enough to ignore unrelated messages and the per-message work
    // is constant. Subscription teardown follows the same
    // takeUntilDestroyed pattern as the other RxJS streams above.
    fromEvent<MessageEvent>(window, 'message')
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((event) => {
        if (event?.data?.type !== 'AI_ORDER_LAUNCHER_STATE') {
          return;
        }
        const expectedOrigin = `https://order-ai.${this.whitelabelService.getWhiteLabelDomainUri()}`;
        if (event.origin !== expectedOrigin) {
          return;
        }
        const state = event.data.state;
        this.aiWidgetLauncherState =
          state === 'chat' || state === 'launcher' ? state : 'collapsed';
      });
  }

  ngAfterViewChecked(): void {
    this.updateBottomChromeHeight();
  }

  async ngOnDestroy() {
    // Clear keyboard listener timeouts
    if (this.keyboardShowTimeout) {
      cancelAnimationFrame(this.keyboardShowTimeout);
    }
    if (this.keyboardHideTimeout) {
      cancelAnimationFrame(this.keyboardHideTimeout);
    }

    // Remove keyboard listeners
    for (const handle of this.androidKeyboardListeners) {
      (await handle).remove();
    }
    this.androidKeyboardListeners = [];
    if (this.keyboardShowListener) {
      (await this.keyboardShowListener).remove();
    }
    if (this.keyboardHideListener) {
      (await this.keyboardHideListener).remove();
    }

    // Remove SafeArea listener
    if (this.safeAreaListener) {
      (await this.safeAreaListener).remove();
    }

    // Remove the autoprint reconciliation lifecycle listener
    if (this.appStateChangeListener) {
      (await this.appStateChangeListener).remove();
    }

    // Remove resize listener
    if (this.resizeListener) {
      window.removeEventListener('resize', this.resizeListener);
    }

    this.removeScrollListener();
    this.messageService.cleanup(); // Clean up message service listeners
    this.renderer.removeStyle(
      document.documentElement,
      BOTTOM_CHROME_HEIGHT_VARIABLE,
      RendererStyleFlags2.DashCase,
    );
  }

  /**
   * Publish how much of the bottom of the viewport is already spoken for by
   * fixed chrome, so `.ai-widget-frame` can be lifted clear of it. Card #11285.
   *
   * Driven from `ngAfterViewChecked` rather than from a subscription because
   * none of the boxes involved announces its own geometry: the customer footer
   * grows when a long store name wraps to a second line, the View Order button
   * appears with the first cart item and moves again when a cookie bar is
   * dismissed, and `StorePageComponent` repositions it imperatively. This is
   * the hook `PublicLayoutComponent` and `StorePageComponent` already use to
   * track exactly these elements, so the widget reads them on the cadence they
   * are written on.
   *
   * The read is gated on the widget actually being on screen, which is the same
   * condition the template applies. For every tenant without the AI Ordering
   * Assistant — and on every route outside the storefront catalog — the gate
   * costs a handful of field reads and touches no layout at all. On the catalog
   * for an AI tenant it does read layout each tick, but this hook runs on the
   * root component, so it runs last: the neighbours above have already forced
   * the same flush, and the write below only happens when the value changed.
   */
  private updateBottomChromeHeight(): void {
    const root = document.documentElement;
    const published = root.style.getPropertyValue(
      BOTTOM_CHROME_HEIGHT_VARIABLE,
    );
    const widgetIsRendered =
      this.isOrdersAiEnabled &&
      !!this.storesAiSafeUrl &&
      this.shouldShowAiWidget() &&
      this.isStoreCatalogPage();

    if (!widgetIsRendered) {
      // Drop the reserve rather than leave it standing: nothing reads it while
      // the widget is gone, and a stale value would place the bubble against
      // the previous page's chrome for the first frame after it comes back.
      if (published) {
        this.renderer.removeStyle(
          root,
          BOTTOM_CHROME_HEIGHT_VARIABLE,
          RendererStyleFlags2.DashCase,
        );
      }
      return;
    }

    const reserve = `${this.measureBottomChromeHeight()}px`;
    // Compared against the live inline value rather than a cached field: the
    // variable is removed again on every route that hides the widget, so a
    // cache would report it as already published and never write it back.
    if (published !== reserve) {
      this.renderer.setStyle(
        root,
        BOTTOM_CHROME_HEIGHT_VARIABLE,
        reserve,
        RendererStyleFlags2.DashCase,
      );
    }
  }

  /**
   * The distance from the bottom of the viewport to the top of the highest
   * piece of bottom chrome currently on screen, in CSS pixels.
   *
   * Taking the topmost edge rather than summing the heights is what makes this
   * correct without knowing how the boxes stack: the View Order button already
   * sits above the cookie bars, which already sit above the customer footer or
   * the tab bar, which already sit above the safe-area inset. Whichever of them
   * reaches highest is the one the widget has to clear, and every offset
   * underneath it — including the ones this list never names — is carried in
   * that single measurement for free.
   */
  private measureBottomChromeHeight(): number {
    const viewportHeight = window.innerHeight;
    const widget = document.querySelector<HTMLElement>(AI_WIDGET_SELECTOR);
    if (!viewportHeight || !widget) {
      return 0;
    }

    // The widget's own column. Only its horizontal extent is read, and that
    // does not depend on the reserve being computed here — `right` is fixed and
    // the width comes from the launcher state — so there is no circularity.
    // Taking it from the element rather than restating `88px` / `right: 20px`
    // keeps the two sides from drifting apart when the stylesheet changes.
    const column = widget.getBoundingClientRect();
    if (column.width <= 0) {
      return 0;
    }

    let reserved = 0;
    document
      .querySelectorAll<HTMLElement>(BOTTOM_CHROME_SELECTORS)
      .forEach((element) => {
        const rect = element.getBoundingClientRect();
        // A collapsed rect is `display: none` — the `d-none` the View Order
        // button carries while the basket is disabled, or the hidden one of the
        // two catalog layouts — or a bar that has not laid out yet. Neither
        // occupies the corner. Checked before the computed style because it is
        // the cheaper read of the two.
        if (rect.height <= 0 || rect.width <= 0 || rect.top >= viewportHeight) {
          return;
        }

        // Chrome that never reaches the widget's column cannot block it. The
        // TILES layout constrains its View Order button to the centred catalog
        // column, so on a wide desktop it stops well short of the corner the
        // bubble sits in and must not lift it.
        if (rect.right <= column.left || rect.left >= column.right) {
          return;
        }

        const style = window.getComputedStyle(element);
        // Only viewport-anchored chrome competes with the widget, and only for
        // a fixed element does `innerHeight - top` mean anything: the same
        // subtraction on a static element in a scrolled page measures where it
        // happens to have scrolled to, not what it covers.
        //
        // `visibility: hidden` is load-bearing, not defensive. It is how the
        // View Order wrapper renders while it has no button to show, and how
        // the customer footer stands down for the soft keyboard: both keep
        // their box and take no taps, so there is nothing left to clear, and
        // reserving for either would strand the bubble above empty screen.
        if (style.position !== 'fixed' || style.visibility === 'hidden') {
          return;
        }

        reserved = Math.max(reserved, viewportHeight - rect.top);
      });

    // Rounded up so a sub-pixel box height can never leave the bubble a
    // fraction of a pixel inside the chrome it is meant to clear.
    return Math.min(
      Math.ceil(reserved),
      Math.max(viewportHeight - MIN_AI_WIDGET_HEADROOM, 0),
    );
  }
  private setSafeAreaInsets(insets: SafeAreaInsets) {
    this.safeAreaInsets = {
      top: insets.top ?? 0,
      bottom: insets.bottom ?? 0,
      left: insets.left ?? 0,
      right: insets.right ?? 0,
    };
    this.applySafeAreaInsets();
  }

  /**
   * Publish the on-screen keyboard height as `--app-keyboard-inset-bottom`.
   *
   * ANDROID API >= 35 ONLY, and the gate is load-bearing:
   *  - API <= 34 — `decorFitsSystemWindows` is true, so the framework resizes
   *    the window for the soft input. `100dvh` already shrinks, and publishing
   *    a height here would subtract the keyboard a SECOND time.
   *  - API >= 35 — the platform forces `decorFitsSystemWindows = false` and
   *    stops resizing for the IME; the app must consume the IME inset itself.
   *    Capacitor's own `SystemBars` would do it (`v.setPadding(0, 0, 0,
   *    imeInsets.bottom)`), but `insetsHandling: "disable"` returns before that
   *    listener is ever installed, and capawesome's EdgeToEdge never handled
   *    the IME at all — so nothing does. That is why the chat input sits behind
   *    the keyboard on Android 15/16 but not on Android 14.
   *  - iOS is unaffected: `resize: native` shrinks the WKWebView, so `100dvh`
   *    already tracks the keyboard there too.
   *
   * The plugin reports the height in CSS px (`Keyboard.java` divides by
   * density), so the value is used as-is.
   */
  private listenToAndroidKeyboardInset(): void {
    const publish = (height: number) =>
      this.ngZone.run(() =>
        document.documentElement.style.setProperty(
          '--app-keyboard-inset-bottom',
          `${height}px`,
        ),
      );

    // `Will*` rather than `Did*` so the layout is correct before the keyboard
    // finishes animating in, matching BottomBarComponent's existing choice.
    this.androidKeyboardListeners.push(
      Keyboard.addListener('keyboardWillShow', (info) =>
        publish(info.keyboardHeight),
      ),
      Keyboard.addListener('keyboardWillHide', () => publish(0)),
    );
  }

  private applySafeAreaInsets(): void {
    const bottomInset = this.isKeyboardOpen ? 0 : this.safeAreaInsets.bottom;
    this.iosSafeAreaOverflowHeight = this.safeAreaInsets.top;
    this.iosSafeAreaBottomHeight = bottomInset;

    const appliedInsets: SafeAreaInsets = {
      ...this.safeAreaInsets,
      bottom: bottomInset,
    };

    (
      Object.entries(appliedInsets) as Array<[keyof SafeAreaInsets, number]>
    ).forEach(([key, value]) => {
      document.documentElement.style.setProperty(
        `--safe-area-inset-${key}`,
        `${value}px`,
      );
    });
  }

  private async signInWithGoogle() {
    try {
      const result = await SocialLogin.login({
        provider: 'google',
        options: {
          scopes: ['email', 'profile'],
          forcePrompt: true,
          style: 'standard',
          autoSelectEnabled: false,
          filterByAuthorizedAccounts: false,
          forceRefreshToken: true,
        },
      });
      if (result.result.responseType === 'online') {
        const googleResponse = result.result as GoogleLoginResponseOnline;

        const user = {
          provider: 'google',
          accessToken: googleResponse.idToken,
          email: googleResponse.profile.email,
          firstName: googleResponse.profile.givenName,
          lastName: googleResponse.profile.familyName,
          mode: this.helperService.isGoAdminApp()
            ? 'admin-login'
            : 'customer-login',
        };

        window.postMessage(user, `${window.location.origin}/login`);
        // Handle user information, e.g., send to your backend or save locally
      }
    } catch (error) {
      console.error('Error during Google sign-in:', error);
    }
  }

  private async appleSignIn() {
    let redirectURI = window.location.href;
    if (!redirectURI.startsWith('https://')) {
      console.error('Redirect URI is not HTTPS');
      // Handle non-HTTPS scenario
    } else {
      const uriParts = redirectURI.split('?');
      redirectURI = uriParts[0];
    }
    const rawNonce = 'nonce';
    const hashedNonceHex = await this.helperService.isSha256(rawNonce);
    let options: SignInWithAppleOptions = {
      clientId: environment.appleClientId,
      redirectURI,
      scopes: 'email name',
      nonce: hashedNonceHex,
    };
    try {
      const response: SignInWithAppleResponse =
        await SignInWithApple.authorize(options);
      if (
        response?.response?.identityToken &&
        response?.response?.authorizationCode
      ) {
        const appleToken = this.jwtHelper.decodeToken(
          response.response.identityToken,
        );
        const socialAccountDetails = {
          provider: 'apple',
          accessToken: response.response.identityToken,
          appleCode: response.response.authorizationCode,
          email: appleToken.email,
          firstName: response.response.givenName,
          lastName: response.response.familyName,
          mode: this.helperService.isGoAdminApp()
            ? 'admin-login'
            : 'customer-login',
        };
        window.postMessage(
          socialAccountDetails,
          `${window.location.origin}/login`,
        );
      }
    } catch (err) {
      console.error('Sign in failed:', err);
      window.frames[0].postMessage({ status: 'error', error: err }, '*');
    }
  }

  private async facebookSignIn() {
    try {
      const result = await SocialLogin.login({
        provider: 'facebook',
        options: {
          permissions: ['email', 'public_profile'],
          limitedLogin: this.isIOSApp ? true : false,
        },
      });

      let user;

      if (Capacitor.getPlatform() === 'ios') {
        // iOS: Use the response directly
        user = this.extractFacebookUserData(
          result.result,
          null,
          this.helperService.isGoAdminApp(),
        );
      } else {
        // Android: Fetch additional profile data
        const profileData = await SocialLogin.providerSpecificCall({
          call: 'facebook#getProfile',
          options: {
            fields: ['id', 'name', 'email', 'first_name', 'last_name'],
          },
        });

        user = this.extractFacebookUserData(
          result.result,
          profileData.profile,
          this.helperService.isGoAdminApp(),
        );
      }

      // Send the user data via postMessage
      window.postMessage({ ...user }, `${window.location.origin}/login`);
    } catch (error) {
      console.error('Error during Facebook sign-in:', error);
    }
  }

  private extractFacebookUserData(
    facebookResponse: any,
    providerProfile?: any,
    isAdminApp: boolean = false,
  ): any {
    // Determine the platform
    const isIOS = Capacitor.getPlatform() === 'ios';

    // Extract profile data based on platform
    const profile = facebookResponse.profile || {};
    const additionalProfile = isIOS
      ? facebookResponse.profile || {}
      : providerProfile || {};

    // Extract common fields with fallbacks
    const email =
      additionalProfile?.email ||
      profile?.email ||
      providerProfile?.email ||
      '';

    const fullName =
      additionalProfile?.name || profile?.name || providerProfile?.name || '';

    const firstName = this.helperService.extractFirstName(fullName);
    const lastName = this.helperService.extractLastName(fullName);

    // Build the user object
    const user = {
      provider: 'facebook',
      accessToken: isIOS
        ? facebookResponse.idToken
        : facebookResponse?.accessToken?.token,
      email: email,
      firstName: firstName,
      lastName: lastName,
      fullName: fullName,
      imageUrl: isIOS
        ? profile?.imageURL || ''
        : providerProfile?.picture?.data?.url || profile?.imageURL || '',
      mode: isAdminApp ? 'admin-login' : 'customer-login',
    };

    // Add Facebook ID for Android
    if (!isIOS) {
      user['facebookId'] = profile?.userID || providerProfile?.id || '';
    }

    return user;
  }

  private initializeDeepLinks() {
    this.addAppUrlOpenListener((data) => {
      const url = data.url;
      let route = this.extractRouteFromUrl(url);
      let routeType = this.extractRouteType(route);
      // Story #11856: a link that opens a page of its own (a payment return,
      // an item, a bar page...) is the launch's destination, which the
      // template app's start page must not replace: the launch is settled,
      // and the Menu's pop-up waits for the Menu as usual. A link to the
      // store itself, such as the web store's "get the app" badge, names no
      // page, so the app opens on its start page as usual.
      const opensPageOfItsOwn = this.opensPageOfItsOwn(routeType, route);
      if (opensPageOfItsOwn) {
        this.initialViewOrchestrator.settleStartPage();
      }

      this.afterBootNavigation(() =>
        this.ngZone.run(() => {
          switch (routeType) {
            case 'api':
              this.handleApiCall(route);
              break;
            case 'thankyou-redirect':
              this.handleThankyouRedirect(route);
              break;
            case 'stripe-verification':
              this.router.navigate([route.path], {
                queryParams: route.queryParams,
              });
              break;
            case 'customer-app':
              this.handleCustomerApp(route, route.fragment);
              break;
            case 'template-app':
              // Story #11856: in an app with a start page, a link to the store
              // itself only opens the app, whose launch shows the start page or
              // keeps the page on screen. Going to the store root would put the
              // Menu and its pop-up there instead, and add a history entry Back
              // would return to.
              if (opensPageOfItsOwn || startPageRoute() === null) {
                this.handleTemplateApp(route);
              }
              break;
            case 'payment-redirect':
              this.router.navigateByUrl(route.path);
              break;
            case 'standalone-payment':
              this.handleStandalonePayment(route);
              break;
            case 'payment-error':
              this.handleGeneralRedirect(route);
              break;
            default:
              console.error('No Route Matched', url);
          }

          this.paymentRedirectService.closedByRedirection = true;
          this.paymentRedirectService.closeMobileBrowser();
        }),
      );
    });
  }

  /**
   * Runs `navigate` once the app's boot navigation has finished. In an app
   * with a start page a link can arrive during the boot redirects (story
   * #11856: its listener is added before the Android splash), and a link
   * navigation started there is overridden by CustomerModeGuard, which only
   * sets the store on the boot's own pass. Other apps run it at once, as
   * before.
   */
  private afterBootNavigation(navigate: () => void): void {
    if (startPageRoute() === null || this.router.navigated) {
      navigate();
      return;
    }
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        take(1),
      )
      .subscribe(() => navigate());
  }

  /** Whether this app listens for links before the Android splash (#11856). */
  private listensForLinksBeforeSplash(): boolean {
    return this.helperService.isGoTemplateApp() && startPageRoute() !== null;
  }

  /** Whether this app handles Android Back for its start page (see below). */
  private handlesBackForStartPage(): boolean {
    return (
      this.helperService.isGoTemplateApp() &&
      startPageRoute() !== null &&
      Capacitor.getPlatform() === 'android'
    );
  }

  /**
   * Android Back in a template app that opens on its start page (story #11856).
   * The app starts with a single history entry, so on the start page there is
   * nothing to go back to, and Capacitor alone would leave the press doing
   * nothing. Back then puts the app in the background, which is what the
   * story asks of the start page. With history it steps back as before: once
   * a listener exists Capacitor no longer does that itself. An open dialog is
   * closed first. The voucher scanner owns Back while it is open.
   */
  private listenToBackButtonInStartPageApp(): void {
    this.addBackButtonListener(({ canGoBack }) => {
      if (this.voucherScannerBackButton.hasActiveListener()) {
        return;
      }
      // An open dialog adds no history entry, so Back closes it first, as a
      // step back used to, rather than leaving it open behind the app.
      const openDialogs = this.dialog.openDialogs;
      if (openDialogs.length > 0) {
        openDialogs[openDialogs.length - 1].close();
        return;
      }
      if (canGoBack) {
        window.history.back();
        return;
      }
      void this.minimizeApp();
    });
  }

  /**
   * `App.addListener('backButton', …)` and `App.minimizeApp()`, on their own
   * so a spec can stand in for them: `App` is a Capacitor plugin Proxy whose
   * methods cannot be spied.
   */
  protected addBackButtonListener(
    listener: (event: BackButtonListenerEvent) => void,
  ): Promise<PluginListenerHandle> {
    return App.addListener('backButton', listener);
  }

  protected minimizeApp(): Promise<void> {
    return App.minimizeApp();
  }

  /**
   * `App.addListener('appUrlOpen', …)`, on its own so a spec can stand in for
   * it: `App` is a Capacitor plugin Proxy whose methods cannot be spied.
   */
  protected addAppUrlOpenListener(
    listener: (event: URLOpenListenerEvent) => void,
  ): Promise<PluginListenerHandle> {
    return App.addListener('appUrlOpen', listener);
  }

  /**
   * Whether an app link opens a page of its own rather than just the app
   * (story #11856). Every kind of link the handler routes does, except a
   * template app link to the store itself: '/public', '/public/<alias>' or
   * '/public/template/<alias>', the app's own boot path, with no fragment.
   */
  private opensPageOfItsOwn(routeType: string, route: DeeplinkRoute): boolean {
    switch (routeType) {
      case 'unknown':
      case 'admin-app':
        return false;
      case 'template-app': {
        if (route.fragment) {
          return true;
        }
        const segments = route.path.split('/').filter(Boolean);
        const isStoreRoot =
          segments[0] === 'public' &&
          (segments.length <= 2 ||
            (segments.length === 3 && segments[1] === 'template'));
        return !isStoreRoot;
      }
      default:
        return true;
    }
  }

  private extractRouteFromUrl(url: string): DeeplinkRoute {
    let urlObj = new URL(url);
    const urlReplaced = urlObj.href.split(`${urlObj.protocol}//`)[1];
    urlObj = new URL(`https://${urlReplaced}`);
    const fragment = urlObj.hash.substring(1);
    const schemeSegments = url.split('://')[0].split('com.gonnaorder.');

    // Define recognized environments
    const recognizedEnvironments = [
      'go',
      'goadmin',
      'godev',
      'goadmindev',
      'kioskdev',
      'kiosk',
    ];

    // Detecting the environment or template based on the scheme
    let appEnvironment, storeAlias;

    if (
      recognizedEnvironments.some((env) => schemeSegments[1]?.includes(env))
    ) {
      appEnvironment = schemeSegments[1]; // Assuming the second part is the environment for customer apps
      const subdomainSegments = urlObj.hostname.split('.');
      storeAlias = subdomainSegments[0]; // Assuming the first segment is always the store alias for customer apps
    } else {
      // If no known environment, it's likely a template app, so the store alias is the second segment
      storeAlias = environment.templateStoreAlias;
      appEnvironment = 'template'; // Indicate that this is a template app environment
    }
    return {
      path: urlObj.pathname,
      queryParams: this.parseQueryParams(urlObj.search),
      fragment,
      storeAlias,
      appEnvironment,
      url: urlObj.href,
    };
  }

  private parseQueryParams(search: string) {
    const params = new URLSearchParams(search);
    const queryParams = {};
    params.forEach((value, key) => {
      queryParams[key] = value;
    });
    return queryParams;
  }

  private extractRouteType(route: any) {
    if (route.path.includes('/api')) {
      return 'api';
    } else if (route.path.includes('/stripe/verification')) {
      return 'stripe-verification';
    } else if (route.url.includes('thankyou')) {
      return 'thankyou-redirect';
    } else if (route.url.includes('paymentError')) {
      return 'payment-error';
    } else if (route.path.includes('/public/customer/store')) {
      return 'customer-app';
    } else if (route.path.includes('/public')) {
      return 'template-app';
    } else if (route.path.includes('payment-redirect')) {
      return 'payment-redirect';
    } else if (route.path.includes('/manager')) {
      return 'admin-app';
    } else if (route.path.includes('/standalone-payment')) {
      return 'standalone-payment';
    } else {
      return 'unknown'; //Default Case if no type matches
    }
  }

  private handleApiCall(route: DeeplinkRoute) {
    this.storesService.checkCompletePaymentStatus(route.path).subscribe(
      (res: any) => {
        const url = new URL(res.redirectUrl);
        this.router.navigateByUrl(url.pathname);
      },
      (err) => {
        this.handleGeneralRedirect(route);
      },
    );
  }

  private handleThankyouRedirect(route: DeeplinkRoute) {
    if (this.helperService.isGoKioskApp()) {
      this.handleGeneralRedirect(route);
    } else if (this.helperService.isGoApp()) {
      this.handleCustomerApp(route, route.fragment);
    } else if (this.helperService.isGoTemplateApp()) {
      this.handleTemplateApp(route);
    } else if (this.helperService.isGoAdminApp()) {
      this.store.dispatch(
        new ClearIntegrationPayment({
          src: 'StoreCheckoutPaymentComponent',
          description: `Payment Status: INITIAL`,
        }),
      );
      this.router
        .navigateByUrl('/manager', { skipLocationChange: true })
        .then(() => {
          this.router.navigateByUrl(
            `${route.path}${route.fragment ? `#${route.fragment}` : ''}`,
          );
        });
    }
  }

  private handleGeneralRedirect(route: DeeplinkRoute) {
    this.router.navigateByUrl('/', { skipLocationChange: false }).then(() => {
      setTimeout(() => {
        this.router.navigateByUrl(`${this.router.url}#${route.fragment}`);
      }, 1000);
    });
  }

  private handleCustomerApp(route: DeeplinkRoute, fragment: string) {
    const storeAlias = route.storeAlias;
    this.actions$
      .pipe(
        ofType<SaveStoreByAliasSuccess>(
          StoreActionType.SaveStoreByAliasSuccess,
        ),
        filter((action) => action.saveMode === StoreSaveMode.DEEPLINK),
        take(1),
      )
      .subscribe((action) => {
        if (action.storeAlias) {
          this.ngZone.run(() => {
            this.store.dispatch(new SetMobileStore(action.storeAlias));
            this.router
              .navigateByUrl('/public/customer', { skipLocationChange: true })
              .then(() => {
                this.router.navigateByUrl(
                  `/public/customer/store/${action.storeAlias}${fragment ? `#${fragment}` : ''}`,
                );
              });
          });
        }
      });
    this.actions$
      .pipe(
        ofType<SaveStoreByAliasFailed>(StoreActionType.SaveStoreByAliasFailed),
        take(1),
      )
      .subscribe((action) => {
        this.ngZone.run(() =>
          this.toastr.info(
            this.translateSer.instant('public.main.storeNotFound'),
            this.translateSer.instant('public.global.information'),
          ),
        );
      });

    if (
      route.fragment.includes('thankyou') ||
      route.fragment.includes('cart')
    ) {
      this.store.dispatch(
        new ClearIntegrationPayment({
          src: 'StoreCheckoutPaymentComponent',
          description: `Payment Status: INITIAL`,
        }),
      );
    }

    this.store.dispatch(
      new SaveStoreByAlias(
        storeAlias,
        `https://${storeAlias}.${environment.envDomain}`,
        StoreSaveMode.DEEPLINK,
      ),
    );
  }

  private handleTemplateApp(route: DeeplinkRoute) {
    if (
      environment.templateStoreAlias &&
      environment.templateStoreAlias == route.storeAlias
    ) {
      this.ngZone.run(() => {
        this.store.dispatch(
          new ClearIntegrationPayment({
            src: 'StoreCheckoutPaymentComponent',
            description: `Payment Status: INITIAL`,
          }),
        );
        let baseUrl = '/';
        if (route.path.includes('/capture')) {
          baseUrl = '/manager';
        }
        this.router
          .navigateByUrl(baseUrl, { skipLocationChange: true })
          .then(() => {
            this.router.navigateByUrl(
              `${route.path}${route.fragment ? `#${route.fragment}` : ''}`,
            );
          });
      });
    }
  }

  private handleStandalonePayment(route: DeeplinkRoute) {
    this.router.navigateByUrl('/', { skipLocationChange: false }).then(() => {
      this.router.navigateByUrl(route.path);
    });
  }

  private addScrollListener(): void {
    const closeDropdowns = (event?: Event) => {
      try {
        if (event && event.target) {
          const scrollTarget = event.target as Element;

          // Don't close if scrolling within the dropdown panel itself
          if (
            scrollTarget.classList &&
            scrollTarget.classList.contains('mat-mdc-select-panel')
          ) {
            return;
          }
        }

        const openSelectPanels = document.querySelectorAll(
          '.mat-mdc-select-panel',
        );
        if (openSelectPanels.length > 0) {
          const selectBackdrops = document.querySelectorAll(
            '.cdk-overlay-backdrop',
          );
          selectBackdrops.forEach((backdrop) => {
            if (backdrop instanceof HTMLElement) {
              const overlayPane = backdrop.nextElementSibling;
              if (
                overlayPane &&
                overlayPane.querySelector('.mat-mdc-select-panel')
              ) {
                backdrop.click();
              }
            }
          });

          const matSelects = document.querySelectorAll('mat-select');
          matSelects.forEach((select) => {
            if (select && (select as any).close) {
              try {
                (select as any).close();
              } catch (e) {}
            }
          });
        }
      } catch (error) {
        console.warn('Error closing dropdowns:', error);
      }
    };

    window.addEventListener('scroll', closeDropdowns, { passive: true });
    document.addEventListener('scroll', closeDropdowns, {
      passive: true,
      capture: true,
    });
  }

  private removeScrollListener(): void {
    if (this.scrollTimeout) {
      clearTimeout(this.scrollTimeout);
    }
  }

  private setBrandColors() {
    const primaryBrandColour =
      this.whitelabelService.getWhiteLabelPrimaryBrandColour();
    const secondaryBrandColour =
      this.whitelabelService.getWhiteLabelSecondaryBrandColour();
    // Set the custom properties for the current domain
    document.documentElement.style.setProperty(
      '--primary-brand-color',
      primaryBrandColour,
      'important',
    );
    document.documentElement.style.setProperty(
      '--secondary-brand-color',
      secondaryBrandColour,
      'important',
    );
    document.title = this.whitelabelService.getWhiteLabelName();
  }

  private pushNotificationReceived(notification) {
    const payload = notification?.data;
    if (!payload) {
      return;
    }

    // The FCM payload is now data-only at the top level so the JS handler runs in
    // the background. On Android, render the user-visible notification ourselves
    // since the FCM SDK no longer auto-displays one. iOS still receives a native
    // alert from aps.alert and must not get a duplicate from us.
    if (this.helperService.isAndroid()) {
      void this.scheduleOrderLocalNotificationUnlessShown(payload);
    }

    this.handleOrderPush(payload, { navigateToOrder: false, print: true });
  }

  /**
   * Ask for notification permission on the first launch and subscribe to the
   * store topic on every launch (#11620, PO decision 7A).
   *
   * The topic is `environment.templateStoreAlias`, the store the app was built
   * for, and never the runtime-selected alias: a brand app lets the customer
   * switch to a child store, and PO decision 1A says only the parent's own
   * promotions are pushed.
   *
   * Deferred until a store has loaded (test case 16). Asked from the
   * platform-ready block instead, the iOS system dialog appears over the splash
   * before the customer has seen anything — which is when people tap "Don't
   * Allow", and iOS then never asks again.
   */
  private enablePromotionNotifications() {
    this.store
      .select(getSelectedStore)
      .pipe(
        // The selector returns a placeholder store (id null) until the real
        // one arrives, so "a store has loaded" means it has an id.
        filter((store) => !!store?.id),
        take(1),
      )
      .subscribe(() => {
        void this.mobileNotificationService.enablePromotionNotifications(
          environment.templateStoreAlias,
        );
      });
  }

  /**
   * The customer tapped a promotion push (#11620).
   *
   * Loads the promotion that was actually pushed — the store payload only
   * carries whichever promotion is eligible right now — and hands it to the
   * existing `store-promotion-popup` through router state, which the component
   * already reads. Going through the view fragment rather than
   * `determineInitialView` is also what makes the pop-up show when the
   * customer already dismissed one this session: they asked for this one.
   *
   * A promotion that ended, was deleted or was deactivated since the push
   * 404s, and the app just opens the store.
   *
   * It waits for the store to be selected before navigating. A tap on a killed
   * app delivers this the moment the JS layer boots, when `router.url` is still
   * `/` and `loadProperView()` has not run — navigating then puts the fragment
   * on the wrong URL and the boot render replaces the pop-up with the catalog.
   *
   * In a brand app the customer may be on a child store when they tap. Then
   * the app first moves to the pushed store and shows the pop-up there. See
   * `openPromotionInPushedStore`.
   */
  private promotionNotificationActionPerformed(action) {
    const payload = action?.notification?.data;
    if (payload?.type !== 'PROMOTION') {
      return;
    }

    const storeId = Number(payload.storeId);
    const promotionId = Number(payload.promotionId);
    if (!storeId || !promotionId) {
      return;
    }

    combineLatest([
      this.storeService.retrievePublicPromotion(storeId, promotionId).pipe(
        take(1),
        catchError(() => of(null)),
      ),
      this.store.select(getSelectedStore).pipe(
        // Not `!!store`: until the store loads the selector returns a
        // placeholder with id null, which would fail the id check below and
        // open the store without the pop-up.
        filter((store) => !!store?.id),
        take(1),
      ),
    ])
      .pipe(take(1))
      .subscribe(([promotion, selectedStore]) => {
        this.ngZone.run(() => {
          if (!promotion) {
            this.openStoreForPushThatOpensNothing();
            return;
          }
          if (selectedStore.id === storeId) {
            // Story #11856: the tap opens this promotion, which the template
            // app's start page must not replace. Only once it is known to
            // open: a push whose promotion is gone opens nothing.
            this.initialViewOrchestrator.skipStartPage();
            this.openPushedPromotion(promotion);
            return;
          }
          this.openPromotionInPushedStore(
            payload.storeAlias,
            storeId,
            selectedStore.id,
            promotion,
          );
        });
      });
  }

  /**
   * A promotion push that opens nothing (its promotion is gone, or it names
   * another store) opens the store, as before. In an app with a start page
   * (story #11856) the launch already decides that: the start page, or the
   * page the customer is on, so going to the store root would only put the
   * Menu and its pop-up in their place.
   */
  private openStoreForPushThatOpensNothing(): void {
    if (startPageRoute() !== null) {
      return;
    }
    this.router.navigateByUrl(this.locationService.public_url());
  }

  /**
   * Shows the pushed promotion on the store on screen. base_url() is built
   * from the router URL, which is the store currently selected.
   */
  private openPushedPromotion(promotion: PromotionData) {
    const promotionUrl = this.locationService.base_url('promotion');
    this.router.navigateByUrl(promotionUrl, {
      state: { promotion },
      // A pop-up opened by an earlier tap is still on this URL. The router
      // skips a navigation to the URL it is on, so that pop-up would keep the
      // old promotion. 'reload' runs the navigation anyway: the route and the
      // pop-up are reused (no guard runs again), and the pop-up takes the new
      // state on NavigationEnd.
      onSameUrlNavigation: 'reload',
      // Replace that pop-up's history entry instead of adding one, so BACK
      // still leaves the pop-up and does not show the old promotion.
      replaceUrl: this.router.url === promotionUrl,
    });
  }

  /**
   * Brand app: the customer switched to a child store and then tapped the
   * parent's push. Move to the pushed store, wait until it has loaded, then
   * show the pop-up, like a tap on a closed app does (story: "opens the app
   * on that store and shows the promotion pop-up").
   *
   * Only the store the app was built for is ever pushed to this app (PO
   * decision 1A: the topic is its alias), so that is the only store a tap may
   * move to. Anything else keeps the current store, with no pop-up.
   *
   * A customer in checkout or in a payment stays where they are: moving
   * would reload the store under them, and a payment screen that goes away
   * never shows whether the order went through. The basket itself is not
   * lost by a move: each store keeps its own `orderUuid-<alias>` cookie.
   */
  private openPromotionInPushedStore(
    storeAlias: string,
    storeId: number,
    currentStoreId: number,
    promotion: PromotionData,
  ) {
    if (!storeAlias || storeAlias !== this.builtForStoreAlias) {
      this.openStoreForPushThatOpensNothing();
      return;
    }
    if (ORDER_IN_PROGRESS_VIEWS.includes(this.storeViewState?.state)) {
      return;
    }
    this.initialViewOrchestrator.skipStartPage();

    this.switchTemplateStore(storeAlias).then(() => {
      this.store
        .select(getSelectedStore)
        .pipe(
          // Skips the store we left and the placeholder (id null) the
          // selector returns while the new store loads.
          filter((store) => !!store?.id && store.id !== currentStoreId),
          take(1),
        )
        .subscribe((store) => {
          if (store.id === storeId) {
            this.ngZone.run(() => this.openPushedPromotion(promotion));
          }
        });
    });
  }

  /**
   * The template app's own store switch, the same steps as the store list
   * and the sibling selector take (store-child-list.component.ts and
   * store-sibling-selector.component.ts, their `isGoTemplateApp()` branch).
   * The `/public/<alias>` route runs AdminStoreLoadingGuard, which resets the
   * in-memory cart and loads the store.
   */
  private switchTemplateStore(storeAlias: string): Promise<boolean> {
    environment.templateStoreAlias = storeAlias;
    this.store.dispatch(new SetMobileStore(storeAlias));
    return this.router
      .navigateByUrl('/', {
        skipLocationChange: true,
        onSameUrlNavigation: 'reload',
      })
      .then(() => this.router.navigateByUrl(`/public/${storeAlias}`));
  }

  private pushNotificationActionPerformed(action) {
    const payload = action?.notification?.data;
    if (!payload) {
      return;
    }

    // Reached when the user taps a notification the OS drew itself: an APNs
    // alert on iOS. Printing is dispatched here too, because the app may never
    // have seen the message live — if it was killed, this tap is the first the
    // JS layer hears of the order.
    this.handleOrderPush(payload, {
      navigateToOrder: action?.actionId === 'tap',
      print: true,
    });
  }

  /**
   * Tap handler for the local notification Android now draws for itself.
   *
   * Without this the banner is inert on Android. The backend no longer sends a
   * notification block, so the OS never creates an FCM notification and
   * `pushNotificationActionPerformed` can no longer fire there — tapping an
   * order did nothing at all.
   *
   * Navigation only, deliberately. This tap can only happen after
   * `pushNotificationReceived` has already run, because that handler is what
   * scheduled the notification being tapped, so the order is already queued.
   * Dispatching again here would put a second copy of the same order in the
   * autoprint queue — the duplicate-receipt failure of card Y2t8inVM.
   */
  private localNotificationActionPerformed(action) {
    const payload = action?.notification?.extra;
    if (!payload) {
      return;
    }

    this.handleOrderPush(payload, {
      navigateToOrder: action?.actionId === 'tap',
      print: false,
    });
  }

  /**
   * Shared tail of every order-push path: resolve the store, optionally open
   * the order, and optionally queue it for printing.
   */
  private handleOrderPush(
    payload: any,
    options: { navigateToOrder: boolean; print: boolean },
  ): void {
    if (!this.helperService.isAndroid() && !this.helperService.isIOS()) {
      this.logger.error('Not supported device');
      return;
    }

    this.resolveStoreForAlias(payload?.storeAlias).subscribe((clientStore) => {
      if (!clientStore) {
        return;
      }

      if (options.navigateToOrder && payload?.uuid) {
        this.ngZone.run(() => {
          this.router
            .navigate([`/manager/stores/${clientStore.id}/orders`])
            .then(() => {
              this.router.navigate([
                `/manager/stores/${clientStore.id}/orders/${payload.uuid}`,
              ]);
            });
        });
      }

      if (options.print) {
        // Dispatch only. The queue drain is triggered from
        // `PrinterService.addToAutoPrintQueue`, at the far end of the effect
        // chain this action starts — draining here found an empty queue every
        // time, because none of that chain has run yet. Marked as the push
        // path, so the queue can drop it when reconciliation already printed
        // or queued this order — the push Capacitor replays when the app is
        // opened after being killed is one such case. Card #4514.
        this.store.dispatch(
          new OrderDirectPrint(payload, clientStore.id, 'push'),
        );
      }
    });
  }

  /**
   * Tells the printers feature when GoAdmin leaves and re-enters the
   * foreground, so an order that arrived while it was away and did not print
   * can be caught on the way back. `PrintersEffects.onAutoprintAppStateChanged`
   * owns what happens next.
   *
   * GoAdmin on Android only, deliberately. Autoprint is a GoAdmin feature, and
   * the record reconciliation relies on to tell a printed order from a lost one
   * — `recentlyPrintedOrders`, written by `PrinterService.drainAutoPrintQueue`
   * — only exists on the Android autoprint queue path; iOS prints through
   * AirPrint (`printContentUrl`) and writes no such record, so reconciling there
   * could only guess, and would trade a lost receipt for a duplicate one. Both
   * defects this fixes are Android-only. Card #4514.
   *
   * Both gates live here rather than at the call site so that moving the call
   * earlier in startup cannot widen them.
   */
  private registerAutoprintReconciliation(): void {
    if (!this.helperService.isAndroid() || !this.helperService.isGoAdminApp()) {
      return;
    }

    this.appStateChangeListener = this.addAppStateListener(({ isActive }) =>
      // Capacitor delivers plugin events outside the Angular zone.
      this.ngZone.run(() =>
        this.store.dispatch(new AutoprintAppStateChanged(isActive)),
      ),
    );
  }

  /**
   * `App.addListener('appStateChange', …)`, on its own so a spec can stand in
   * for it: `App` is a Capacitor plugin Proxy whose methods cannot be spied.
   */
  protected addAppStateListener(
    listener: (state: { isActive: boolean }) => void,
  ): Promise<PluginListenerHandle> {
    return App.addListener('appStateChange', listener);
  }

  /**
   * Finds the store an order push belongs to, by exact alias.
   *
   * `/api/v1/stores/search` matches `aliasName` with a substring
   * `LIKE '%alias%'` and applies no sort, so its results are every store whose
   * alias merely CONTAINS the one we want, in arbitrary order. Taking
   * `data[0]` therefore resolved an arbitrary store whenever one alias was a
   * substring of another — "acme" also matching "acme-2" — and the autoprint
   * subscription lookup that follows then read the wrong store's flag and
   * silently printed nothing, while manual printing kept working because it
   * uses the store already on screen.
   *
   * Match exactly, or resolve nothing and log why.
   */
  private resolveStoreForAlias(storeAlias: string): Observable<any | null> {
    if (!storeAlias) {
      this.logger.error('Order push carried no storeAlias; ignoring it.');
      return of(null);
    }

    return this.storesService.list(defaultPaging, storeAlias).pipe(
      take(1),
      map((result) => {
        const matches = result?.data ?? [];
        const store = matches.find((s) => s.aliasName === storeAlias);
        if (!store) {
          this.logger.error(
            `Order push for store alias "${storeAlias}" matched ` +
              `${matches.length} store(s) but none exactly; ignoring it.`,
          );
          return null;
        }
        return store;
      }),
      catchError((err) => {
        this.logger.error(
          `Could not resolve store "${storeAlias}" for an order push: ` +
            JSON.stringify(err),
        );
        return of(null);
      }),
    );
  }

  // Build a stable non-negative 31-bit notification id from the order UUID so a
  // duplicate push for the same order does not spawn a second banner. The bit
  // mask is safer than Math.abs, which overflows on Integer.MIN_VALUE.
  private orderNotificationId(uuid: string): number {
    let hash = 0;
    for (let i = 0; i < uuid.length; i++) {
      hash = (hash * 31 + uuid.charCodeAt(i)) | 0;
    }
    return hash & 0x7fffffff;
  }

  /**
   * The tenant's brand colour, for the order banner's icon tint.
   *
   * The FCM SDK used to apply this from `AndroidNotification.setColor`, which was
   * one hardcoded teal for all 32 tenants; sourcing it from the white-label config
   * makes each brand's banner its own. The value is already resolved — an
   * APP_INITIALIZER awaits `loadWhiteLabelData()` and only resolves once the
   * config is in, so this cannot be pending by the time a push arrives.
   *
   * Validated rather than passed through, because the two failure modes are not
   * symmetric: an unresolvable icon name degrades to a default and the banner
   * still posts, but an unparseable colour makes the plugin reject and `return`
   * before it ever calls `notify()` — the order would simply never appear. The
   * value comes from a remote config server outside both repos, so a stray
   * `273773` must cost the tint and nothing more. Undefined is dropped by
   * JSON.stringify, leaving the plugin's own null path.
   */
  private orderNotificationIconColour(): string | undefined {
    const colour = this.whitelabelService.getWhiteLabelPrimaryBrandColour();
    if (!ANDROID_HEX_COLOUR.test(colour ?? '')) {
      this.logger.error(
        `White-label primary brand colour "${colour}" is not a hex colour ` +
          `Android can parse; the order notification will be left untinted.`,
      );
      return undefined;
    }
    return colour;
  }

  /**
   * The tenant's own mark, for the order banner's large icon.
   *
   * The large icon and not the small one, and that is not a compromise — the two
   * slots are rendered differently. Android masks the small icon to its alpha
   * and floods it with the accent colour (`processSmallIconColor` sets
   * `colorable` unconditionally once targetSdk is 21 or above, and
   * `CachingIconView` applies SRC_ATOP), so only a white-on-transparent
   * silhouette survives it. The white-label favicons are filled shapes —
   * gonnaorder and point2order are full-bleed discs, thexo a rounded square,
   * ordergurus a solid square — so as a small icon each would come out a solid
   * tinted blob, worse than the shared glyph. `processLargeLegacyIcon` is gated
   * on the same legacy check, so the large icon keeps its colour untouched.
   *
   * This is a bare res/drawable resource NAME, not a path: the plugin resolves
   * it with `getIdentifier(name, "drawable", pkg)` and accepts neither a path
   * nor a URL, so the copy of this very PNG inside the APK under assets/public/
   * is unreachable from here. `copyWhitelabelNotificationIcons` in
   * capacitor-build-utils.js puts a copy in res/drawable-nodpi at build time.
   *
   * A name that resolves to nothing is harmless rather than an error:
   * `decodeResource` swallows the miss and returns null, and `setLargeIcon(null)`
   * is a no-op, so a tenant with no favicon folder gets exactly today's banner.
   */
  private notificationBrandIcon(): string | undefined {
    // The same string FaviconService uses as the favicon folder name, so the
    // banner artwork and the web favicon cannot drift apart.
    const code = this.whitelabelService.getWhiteLabelCode();
    const slug = (code ?? '').toLowerCase().replace(/[^a-z0-9]/g, '_');
    if (!ANDROID_RESOURCE_NAME.test(slug)) {
      return undefined;
    }
    return `notif_brand_${slug}`;
  }

  /**
   * The banner this app draws for an incoming order.
   *
   * Kept separate from the call that schedules it so its shape can be asserted:
   * a Capacitor plugin is a Proxy whose `get` trap synthesises a fresh function
   * on every property read, so a spy placed on `LocalNotifications` is written
   * to the proxy's target and never read back. The plugin cannot be observed
   * from a spec; this can.
   */
  private buildOrderNotification(payload: any): LocalNotificationSchema {
    return {
      id: this.orderNotificationId(payload.uuid || `${Date.now()}`),
      title: payload?.title ?? '',
      body: payload?.body ?? '',
      // No `smallIcon` here. The resource NAME is the same for every white-label
      // build — Trapeze always writes the asset as fcm_push_icon — so it belongs
      // in capacitor.config.json next to the copy that satisfies it, not in a
      // literal here. Only the asset behind the name is per-brand, and today all
      // the admin apps share one.
      iconColor: this.orderNotificationIconColour(),
      largeIcon: this.notificationBrandIcon(),
      // The channel owns the sound from Android 8 on, and minSdk is 26, so a
      // per-notification `sound` could never be applied. This channel is created
      // with `order_notification` by MobilePushNotificationService.
      channelId: 'fcm_default_channel',
      // Carried through so the tap handler can resolve the store and open the
      // order. A local notification has no `data`, so without this the payload
      // is lost the moment the banner is drawn.
      extra: payload,
      // No `schedule`, so the banner is posted at once and never needs an
      // exact alarm. The plugin defaults this to true from 8.3.0, which made
      // Android 12+ open the "Alarms & reminders" page on every order when the
      // permission was off (the default from Android 14). See card #11721.
      isExactNotification: false,
    };
  }

  /**
   * The order banner, unless the app's native messaging service already showed
   * it (card #4514).
   *
   * With the app closed there is no web app to draw the banner, so
   * OrderPushMessagingService (written into the Android project by
   * capacitor-build-utils.js) draws it, notes the order here and leaves the push
   * for Capacitor to replay once the app starts — which lands in this handler.
   * Drawing it again then would post the same order a second time, with its
   * sound, after staff had already seen it.
   */
  private async scheduleOrderLocalNotificationUnlessShown(
    payload: any,
  ): Promise<void> {
    if (await this.takeNativelyNotifiedOrder(payload?.uuid)) {
      return;
    }
    await this.scheduleOrderLocalNotification(payload);
  }

  /**
   * True, once, for an order the native service notified in the last
   * ORDER_NOTIFIED_NATIVELY_TTL_MS. Older entries are dropped as they are read:
   * one left behind — an order whose push was not the one replayed — must not
   * silence the banner of a later push for the same order.
   */
  private async takeNativelyNotifiedOrder(uuid: unknown): Promise<boolean> {
    if (typeof uuid !== 'string' || !uuid) {
      return false;
    }
    try {
      const stored = await this.mobileStorage.get(ORDER_NOTIFIED_NATIVELY_KEY);
      if (!Array.isArray(stored)) {
        return false;
      }
      const now = Date.now();
      const fresh = stored.filter(
        (entry) =>
          typeof entry?.uuid === 'string' &&
          Number.isFinite(entry?.at) &&
          now - entry.at < ORDER_NOTIFIED_NATIVELY_TTL_MS,
      );
      const shown = fresh.some((entry) => entry.uuid === uuid);
      const kept = fresh.filter((entry) => entry.uuid !== uuid);
      if (kept.length !== stored.length) {
        await this.mobileStorage.set(ORDER_NOTIFIED_NATIVELY_KEY, kept);
      }
      return shown;
    } catch (err) {
      this.logger.error('Could not read the natively notified orders', err);
      return false;
    }
  }

  /**
   * Leaves this app's order-banner branding where the native messaging service
   * can read it (Capacitor Preferences), so the banner it draws while the app is
   * closed carries the same colour and brand mark as the one drawn here. Both
   * values come from the white-label config, which only the web app loads.
   * Card #4514.
   */
  private async rememberOrderNotificationBranding(): Promise<void> {
    try {
      await this.mobileStorage.set(ORDER_NOTIFICATION_BRANDING_KEY, {
        iconColor: this.orderNotificationIconColour() ?? '',
        largeIcon: this.notificationBrandIcon() ?? '',
      });
    } catch (err) {
      this.logger.error('Could not store the order notification branding', err);
    }
  }

  private async scheduleOrderLocalNotification(payload: any): Promise<void> {
    if (!payload?.title && !payload?.body) {
      return;
    }
    try {
      await LocalNotifications.schedule({
        notifications: [this.buildOrderNotification(payload)],
      });
    } catch (err) {
      this.logger.error('LocalNotifications.schedule failed', err);
    }
  }

  /**
   * Predicate for whether the AI Ordering Assistant iframe should be
   * embedded for the currently-selected store. The widget mutates the
   * cart on the customer's behalf, so it is only meaningful when the
   * store both has the AI capability turned on AND lets customers
   * actually order. The two ordering-related settings are
   * default-on, so we hide the widget only on an explicit `false`.
   *
   * Conditions (AND):
   *   1. Tenant has the AI Ordering capability (whitelabel flag).
   *   2. Store has the AI Ordering Assistant toggle on.
   *   3. Store has the Ordering toggle on (`ENABLE_ORDERING !== false`).
   *   4. Store has the Shopping Basket toggle on (`BASKET_ENABLED !== false`).
   *
   * The `!== false` form matches the convention used by
   * `view-order-button.component.ts` and treats a missing setting as
   * the default-on value the GO main backend ships with.
   *
   * See Trello #9734.
   */
  private shouldShowOrdersAi(selectedStore: ClientStore | null): boolean {
    if (!this.isOrdersAiEnabled) return false;
    if (!selectedStore?.id) return false;
    const settings = selectedStore.settings;
    if (!settings?.ORDERS_AI_ENABLED) return false;
    if (settings.ENABLE_ORDERING === false) return false;
    if (settings.BASKET_ENABLED === false) return false;
    return true;
  }

  setupStoresAi(selectedStore: ClientStore, cartState: Order) {
    let baseUrl =
      'https://{domain}?storeId={storeId}&orderId={orderId}&language={language}&domain={appDomain}&refreshToken={refreshToken}';

    const availableLanguages = ['en', 'el'];
    const adminHostUrl = this.whitelabelService.getAdminHostURL();
    const env =
      environment.name == 'production'
        ? 'prod'
        : environment.name == 'staging'
          ? 'staging'
          : 'dev';
    const storeId = selectedStore.id;
    let language = this.translateSer.currentLang;
    let domainUri = `order-ai.${this.whitelabelService.getWhiteLabelDomainUri()}`;

    if (!availableLanguages.includes(language)) {
      language = 'en';
    }

    // Per-page-load nonce so the AI ordering backend invalidates its
    // cached catalog the first time it sees this token (card #9803).
    // crypto.randomUUID exists on all evergreen browsers and on
    // iOS 15.4+ / Android Chromium WebViews shipped from late 2022;
    // the fallback covers older Capacitor builds running on legacy
    // OS versions. The token is not a secret — uniqueness per
    // page-load is the only requirement.
    const refreshToken =
      typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
        ? crypto.randomUUID()
        : `${Date.now()}-${Math.floor(Math.random() * 1e9)}`;

    const url = baseUrl
      .replace('{domain}', domainUri)
      .replace('{orderId}', cartState.uuid)
      .replace('{storeId}', storeId.toString())
      .replace('{language}', language)
      .replace('{appDomain}', adminHostUrl)
      .replace('{refreshToken}', refreshToken);

    this.storesAiSafeUrl = this.sanitizer.bypassSecurityTrustResourceUrl(url);
    this.currentOrderUuid = cartState.uuid;
  }

  isStoreCatalogPage() {
    return this.storeViewState?.state === 'LOADED';
  }

  /**
   * Whether the AI Ordering Assistant iframe should be rendered for the
   * current route.
   *
   * The widget is visible on customer-storefront routes (the public site
   * the customer uses to browse and order) and on the Admin Order Capture
   * page (the kiosk-like admin flow under `/manager/.../capture/...`).
   *
   * It must NOT be visible elsewhere in the admin shell — without this
   * guard, the iframe stays mounted at the root `<body>` selector once the
   * store view state has reached `LOADED` and follows the admin around
   * Settings / Catalog / Marketing pages (card #9684 defect B).
   *
   * The existing tenant / per-store gates upstream of this method
   * (`isOrdersAiEnabled && storesAiSafeUrl` in the template) keep this
   * predicate cheap for non-AI tenants: the `@if` short-circuits before
   * this method runs.
   */
  shouldShowAiWidget(): boolean {
    const url = this.router.url ?? '';
    const isAdminShell = url.includes('/manager/');
    if (!isAdminShell) {
      return true;
    }
    return this.locationService.isAdminOrderCaptureUpdate();
  }
}
